import hybridStorage from './hybridStorage';

/**
 * Iframe Navigation Guard
 *
 * Prevents users from opening links in new tabs when the dashboard runs
 * inside an iframe (embeddable mode). New tabs would break out of the
 * iframe context and require re-authentication.
 */

/**
 * Detects if the app is running inside an iframe or embeddable context
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top || hybridStorage.isEmbeddable();
  } catch (e) {
    // Cross-origin iframe will throw, meaning we ARE in an iframe
    return true;
  }
};

/**
 * Check if a URL is internal (same origin as the current page)
 */
const isInternalLink = (href) => {
  if (!href) return false;
  // Hash-only links are internal
  if (href.startsWith('#')) return true;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Handle navigation for a link in iframe mode
 * Internal links navigate in same frame, external links are blocked
 */
const handleIframeLinkNavigation = (href) => {
  if (!href) return;

  if (isInternalLink(href)) {
    // Hash-only links should not trigger full navigation
    if (href.startsWith('#')) {
      window.location.hash = href;
    } else {
      window.location.href = href;
    }
  } else {
    // Block external links and warn
    if (process.env.NODE_ENV === 'development') {
      console.warn('[IframeGuard] External navigation blocked:', href);
    }
  }
};

/**
 * Initialize navigation guards for iframe/embeddable mode
 * Call this once in App.js after the app mounts
 * @returns {Function} Cleanup function to remove event listeners
 */
export const initIframeNavigationGuard = () => {
  if (!isInIframe()) return () => {};

  // 1. Intercept all link clicks with target="_blank" or modifier keys
  const clickHandler = (e) => {
    const link = e.target.closest('a');
    if (link && (link.target === '_blank' || e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      handleIframeLinkNavigation(link.href);
    }
  };

  // 2. Disable right-click context menu on links (prevents "Open in new tab")
  const contextMenuHandler = (e) => {
    const link = e.target.closest('a');
    if (link) {
      e.preventDefault();
    }
  };

  // 3. Intercept middle-click (wheel button)
  const auxClickHandler = (e) => {
    if (e.button === 1) { // Middle click
      const link = e.target.closest('a');
      if (link) {
        e.preventDefault();
        handleIframeLinkNavigation(link.href);
      }
    }
  };

  // Add event listeners
  document.addEventListener('click', clickHandler, true);
  document.addEventListener('contextmenu', contextMenuHandler, true);
  document.addEventListener('auxclick', auxClickHandler, true);

  // 4. Override window.open to navigate in same frame
  const originalOpen = window.open;
  window.open = (url, target, features) => {
    if (url) {
      handleIframeLinkNavigation(url.toString());
    }
    return null;
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('[IframeGuard] Navigation guards initialized');
  }

  // Return cleanup function
  return () => {
    document.removeEventListener('click', clickHandler, true);
    document.removeEventListener('contextmenu', contextMenuHandler, true);
    document.removeEventListener('auxclick', auxClickHandler, true);
    window.open = originalOpen;
    if (process.env.NODE_ENV === 'development') {
      console.log('[IframeGuard] Navigation guards cleaned up');
    }
  };
};
